<template>
  <Transition name="mobile-select-sheet-fade">
    <div
      v-if="visible"
      class="mobile-select-sheet-backdrop"
      @click="close"
    />
  </Transition>

  <Transition name="mobile-select-sheet-slide">
    <div
      v-if="visible"
      class="mobile-select-sheet"
      :class="{ 'mobile-select-sheet--dragging': dragState.isDragging }"
      :style="sheetInlineStyle"
      @click.stop
    >
      <div class="mobile-select-sheet__content">
        <div
          class="mobile-select-sheet__handle"
          @touchstart.passive="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
          @touchcancel="onTouchCancel"
        />

        <div v-if="searchable" class="mobile-select-sheet__search">
          <input
            v-model="query"
            @input="onSearchInput"
            type="text"
            class="form-control"
            :placeholder="searchPlaceholder"
          />
          <i class="material-icons-outlined fsz-18">search</i>
        </div>

        <div
          class="mobile-select-sheet__list"
          :class="{ 'mobile-select-sheet__list--auto-height': autoListHeight }"
        >
          <template v-for="item in renderedItems" :key="item.key">
            <div v-if="item.type === 'group'" class="mobile-select-sheet__group">
              {{ item.label }}
            </div>
            <div
              v-else
              class="mobile-select-sheet__item"
              @click="onOptionRowClick(item.option, $event)"
            >
              <input
                :type="resolvedInputType"
                class="form-check-input m-0"
                :checked="isSelected(item.option)"
                @click.stop
                @change.stop="toggleOption(item.option)"
              />
              <span>{{ getOptionLabel(item.option) }}</span>
            </div>
          </template>
          <div v-if="!hasRenderableOptions" class="mobile-select-sheet__empty">
            {{ emptyText }}
          </div>
        </div>

        <div class="mobile-select-sheet__footer">
          <button
            type="button"
            class="btn btn-solid-gray-400 small-btn w-100"
            @click="clearDraft"
          >
            Effacer
          </button>
          <button
            type="button"
            class="btn btn-solid-primary-600 small-btn w-100 d-flex justify-content-center"
            :disabled="pending"
            @click="applyDraft"
          >
            <div
              v-if="pending"
              class="spinner-border text-white"
              role="status"
            ></div>
            <span v-else>Appliquer</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    visible: boolean;
    modelValue: any[] | any | null | undefined;
    options: any[];
    optionLabel?: string;
    optionValue?: string;
    optionGroupChildren?: string;
    optionGroupLabel?: string;
    multiple?: boolean;
    inputType?: "checkbox" | "radio";
    allowCustomValue?: boolean;
    searchable?: boolean;
    serverSearch?: boolean;
    searchPlaceholder?: string;
    emptyText?: string;
    maxOptions?: number;
    pending?: boolean;
    autoListHeight?: boolean;
  }>(),
  {
    optionLabel: "name",
    optionValue: "",
    optionGroupChildren: "",
    optionGroupLabel: "",
    multiple: true,
    allowCustomValue: false,
    searchable: true,
    serverSearch: false,
    searchPlaceholder: "Rechercher...",
    emptyText: "Aucun résultat",
    maxOptions: 0,
    pending: false,
    autoListHeight: false,
  }
);

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "update:modelValue", value: any[] | any | null): void;
  (e: "apply", value: any[] | any | null): void;
  (e: "clear"): void;
  (e: "search", value: string): void;
}>();

const query = ref("");
const draft = ref<any[]>([]);
const isVisible = toRef(props, "visible");
const resolvedInputType = computed<"checkbox" | "radio">(() => {
  if (props.inputType) return props.inputType;
  return props.multiple ? "checkbox" : "radio";
});

type GroupedOptions = {
  key: string;
  label: string;
  options: any[];
};

const getOptionLabel = (option: any): string => {
  if (option == null) return "";
  if (typeof option === "object") {
    const labelValue = props.optionLabel ? option?.[props.optionLabel] : option?.name;
    return labelValue == null ? "" : String(labelValue);
  }
  return String(option);
};

const getOptionValue = (option: any): any => {
  if (!props.optionValue) return option;
  if (option && typeof option === "object") return option?.[props.optionValue];
  return option;
};

const toKey = (value: any): string => {
  if (value == null) return "";
  if (typeof value === "object") {
    if ("id" in value) return String(value.id);
    if ("value" in value) return String(value.value);
    if ("name" in value) return String(value.name);
    return JSON.stringify(value);
  }
  return String(value);
};

const getOptionKey = (option: any): string => {
  const comparableValue = getOptionValue(option);
  return toKey(comparableValue);
};

const getGroupLabel = (group: any): string => {
  if (!group || typeof group !== "object") return "";

  const candidateKeys = [
    props.optionGroupLabel,
    "make",
    "label",
    "name",
  ].filter(Boolean) as string[];

  for (const key of candidateKeys) {
    const value = group?.[key];
    if (value == null) continue;
    const label = String(value).trim();
    if (label) return label;
  }

  return "";
};

const groupedOptions = computed<GroupedOptions[]>(() => {
  const rawOptions = Array.isArray(props.options) ? props.options : [];

  if (!props.optionGroupChildren) {
    return [
      {
        key: "group-0",
        label: "",
        options: rawOptions,
      },
    ];
  }

  const childKey = props.optionGroupChildren;

  return rawOptions.map((entry: any, index: number) => {
    const isGroupObject =
      entry &&
      typeof entry === "object" &&
      Array.isArray(entry[childKey]);

    const options = isGroupObject ? entry[childKey] : [entry];
    const label = isGroupObject ? getGroupLabel(entry) : "";
    const keyBase = label || index;

    return {
      key: `group-${toKey(keyBase)}`,
      label,
      options: Array.isArray(options) ? options : [],
    };
  });
});

const modelAsArray = (value: any): any[] => {
  if (props.multiple) return Array.isArray(value) ? [...value] : [];
  if (value == null || value === "") return [];
  return [value];
};

const buildDraftFromModel = () => {
  draft.value = modelAsArray(props.modelValue);
  if (props.allowCustomValue && !props.multiple && typeof props.modelValue === "string") {
    query.value = props.modelValue;
  } else {
    query.value = "";
  }
  resetDrag();
};

const selectedKeySet = computed(() => {
  return new Set(draft.value.map((entry: any) => toKey(getOptionValue(entry))));
});

const normalizeForSearch = (value: string): string => {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
};

const filteredGroupedOptions = computed<GroupedOptions[]>(() => {
  const baseGroups = groupedOptions.value;
  const search = normalizeForSearch(query.value.trim());

  const matches = props.serverSearch
    ? baseGroups
    : (search
      ? baseGroups
        .map((group) => ({
          ...group,
          options: group.options.filter((entry: any) =>
            normalizeForSearch(getOptionLabel(entry)).includes(search)
          ),
        }))
      : baseGroups);

  const nonEmptyGroups = matches.filter((group) => group.options.length > 0);

  if (props.maxOptions && props.maxOptions > 0) {
    let remaining = props.maxOptions;
    const limitedGroups: GroupedOptions[] = [];

    for (const group of nonEmptyGroups) {
      if (remaining <= 0) break;
      const limitedOptions = group.options.slice(0, remaining);
      if (!limitedOptions.length) continue;

      limitedGroups.push({
        ...group,
        options: limitedOptions,
      });
      remaining -= limitedOptions.length;
    }

    return limitedGroups;
  }

  return nonEmptyGroups;
});

type RenderItem =
  | { type: "group"; key: string; label: string }
  | { type: "option"; key: string; option: any };

const renderedItems = computed<RenderItem[]>(() => {
  const items: RenderItem[] = [];
  const showGroupHeaders = Boolean(props.optionGroupChildren);

  filteredGroupedOptions.value.forEach((group, groupIndex) => {
    if (showGroupHeaders && group.label) {
      items.push({
        type: "group",
        key: `${group.key}-header-${groupIndex}`,
        label: group.label,
      });
    }

    group.options.forEach((option, optionIndex) => {
      items.push({
        type: "option",
        key: `${group.key}-option-${getOptionKey(option)}-${optionIndex}`,
        option,
      });
    });
  });

  return items;
});

const hasRenderableOptions = computed(() =>
  filteredGroupedOptions.value.some((group) => group.options.length > 0)
);

const isSelected = (option: any): boolean => {
  return selectedKeySet.value.has(getOptionKey(option));
};

const toggleOption = (option: any) => {
  const optionKey = getOptionKey(option);
  const currentIndex = draft.value.findIndex(
    (entry: any) => toKey(getOptionValue(entry)) === optionKey
  );

  if (props.multiple) {
    if (currentIndex >= 0) {
      draft.value.splice(currentIndex, 1);
      return;
    }
    draft.value = [...draft.value, props.optionValue ? getOptionValue(option) : option];
    return;
  }

  draft.value = [props.optionValue ? getOptionValue(option) : option];
  if (props.allowCustomValue) {
    query.value = getOptionLabel(option);
  }
};

const onOptionRowClick = (option: any, event: MouseEvent) => {
  const target = event.target as HTMLElement | null;
  if (target?.closest("input")) return;
  toggleOption(option);
};

const clearDraft = () => {
  draft.value = [];
  emit("clear");
};

const applyDraft = () => {
  const nextValue = props.multiple ? [...draft.value] : draft.value[0] ?? null;
  emit("update:modelValue", nextValue);
  emit("apply", nextValue);
  close();
};

const close = () => {
  query.value = "";
  emit("update:visible", false);
};

const onSearchInput = () => {
  if (props.allowCustomValue && !props.multiple) {
    const typed = query.value.trim();
    draft.value = typed ? [typed] : [];
  }
  emit("search", query.value);
};

const {
  dragState,
  sheetInlineStyle,
  resetDrag,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTouchCancel,
} = useMobileSheetInteractions({
  visible: isVisible,
  onClose: close,
});

watch(
  isVisible,
  (nextVisible) => {
    if (nextVisible) {
      buildDraftFromModel();
    }
  }
);

watch(
  () => props.modelValue,
  () => {
    if (!isVisible.value) return;
    buildDraftFromModel();
  }
);

</script>
